import { Injectable } from "@angular/core";
import { AppwriteDatabaseService } from '@prism/angular-shared';
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { InventoryTransaction, MetalsHardwareItem, MetalsProductBillOfMaterialsLineItemType, MetalsProductVersion, MetalsProductVersionStatus, Resource, SaveStatus, StampingTask } from "@prism/prism-shared";
import { catchError, defaultIfEmpty, forkJoin, map, mergeMap, Observable, of, pipe, switchMap, withLatestFrom } from "rxjs";
import { MetalsActions, MetalsSelectors } from ".";
import { MetalsApiService } from "../../../services/metals-api.service";
import { PrismCommonApiService } from "../../../services/prism-common-api.service";
import { CoreSelectors } from "../../core";


@Injectable({ providedIn: 'root' })
export class MetalsEffects {

    constructor(
        public store: Store,
        private actions$: Actions,
        private metalsApiService: MetalsApiService,
        private commonApiService: PrismCommonApiService,
    ) { }

    private appendTimestamp<T>(
        accessor: (obj: Record<string, T>) => T,
    ) {
        return pipe(
            map(accessor),
            withLatestFrom(this.store.select(CoreSelectors.getCurrentUser)),
            map(([entity, currentUser]) => ({
                ...entity,
                lastSavedOn: new Date(),
                lastSavedById: currentUser.id
            }))
        )
    }


    LoadHardwareItems$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadHardwareItems.initial),
        mergeMap(() => this.metalsApiService.loadHardwareItems().pipe(
            map((hardwareItems) => MetalsActions.LoadHardwareItems.success({ hardwareItems })),
            catchError((error) => of(MetalsActions.LoadHardwareItems.failure({ error })))
        ))
    ));

    LoadHardwareItemById$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadHardwareItemById.initial),
        mergeMap(({ hardwareItemId }) => this.metalsApiService.loadHardwareItemById(hardwareItemId).pipe(
            map((hardwareItem) => MetalsActions.LoadHardwareItemById.success({ hardwareItem })),
            catchError((error) => of(MetalsActions.LoadHardwareItemById.failure({ error })))
        ))
    ));

    LoadHardwareItemsByType$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadHardwareItemsByType.initial),
        mergeMap(({ hardwareItemType }) => this.metalsApiService.loadHardwareItemsByType(hardwareItemType).pipe(
            map((hardwareItems) => MetalsActions.LoadHardwareItemsByType.success({ hardwareItems })),
            catchError((error) => of(MetalsActions.LoadHardwareItemsByType.failure({ error })))
        ))
    ));

    LoadHardwareItemsByIds$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadHardwareItemsByIds.initial),
        mergeMap(({ hardwareItemIds }) => this.metalsApiService.loadHardwareItemsByIds(hardwareItemIds).pipe(
            map((hardwareItems) => MetalsActions.LoadHardwareItemsByIds.success({ hardwareItems })),
            catchError((error) => of(MetalsActions.LoadHardwareItemsByIds.failure({ error })))
        ))
    ));

    LoadMetalsLaserMaterials$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadMetalsLaserMaterials.initial),
        mergeMap(() => this.metalsApiService.loadMetalsLaserMaterials().pipe(
            map((metalsLaserMaterials) => MetalsActions.LoadMetalsLaserMaterials.success({ metalsLaserMaterials })),
            catchError((error) => of(MetalsActions.LoadMetalsLaserMaterials.failure({ error })))
        ))
    ));

    SetHardwareItem$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.SetHardwareItem.initial),
        this.appendTimestamp<MetalsHardwareItem>(({ hardwareItem }) => hardwareItem),
        mergeMap((hardwareItem) => this.metalsApiService.setHardwareItem(hardwareItem).pipe(
            map(() => MetalsActions.SetHardwareItem.success({ hardwareItem })),
            catchError((error) => of(MetalsActions.SetHardwareItem.failure({ error })))
        ))
    ));

    LoadContacts$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadContacts.initial),
        mergeMap(() => this.metalsApiService.loadContacts().pipe(
            map((contacts) => MetalsActions.LoadContacts.success({ contacts })),
            catchError((error) => of(MetalsActions.LoadContacts.failure({ error })))
        ))
    ));

    LoadContactById$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadContactById.initial),
        mergeMap(({ contactId }) => this.metalsApiService.loadContactById(contactId).pipe(
            map((contact) => MetalsActions.LoadContactById.success({ contact })),
            catchError((error) => of(MetalsActions.LoadContactById.failure({ error })))
        ))
    ));

    SetContact$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.SetContact.initial),
        // Need to add correct timestamps to customer
        // this.appendTimestamp<MetalsCustomer>(({customer}) => customer),
        mergeMap(({ contact }) => this.metalsApiService.setContact(contact).pipe(
            map(() => MetalsActions.SetContact.success({ contact })),
            catchError((error) => of(MetalsActions.SetContact.failure({ error })))
        ))
    ));

    LoadTags$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadTags.initial),
        mergeMap(() => this.metalsApiService.loadTags().pipe(
            map((tags) => MetalsActions.LoadTags.success({ tags })),
            catchError((error) => of(MetalsActions.LoadTags.failure({ error })))
        ))
    ));

    LoadVendorHardwareData$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadVendorHardwareData.initial),
        mergeMap(() => this.metalsApiService.loadVendorHardwareData().pipe(
            map((vendorHardwareData) => MetalsActions.LoadVendorHardwareData.success({ vendorHardwareData })),
            catchError((error) => of(MetalsActions.LoadVendorHardwareData.failure({ error })))
        ))
    ));

    LoadVendorHardwareDataByHardwareItemId$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadVendorHardwareDataByHardwareItemId.initial),
        mergeMap(({ hardwareItemId }) => this.metalsApiService.loadVendorHardwareDataByHardwareItemId(hardwareItemId).pipe(
            map((vendorHardwareData) => MetalsActions.LoadVendorHardwareDataByHardwareItemId.success({ vendorHardwareData })),
            catchError((error) => of(MetalsActions.LoadVendorHardwareDataByHardwareItemId.failure({ error })))
        ))
    ));

    LoadVendorHardwareDataByHardwareItemIds$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadVendorHardwareDataByHardwareItemIds.initial),
        mergeMap(({ hardwareItemIds }) => this.metalsApiService.loadVendorHardwareDataByHardwareItemIds(hardwareItemIds).pipe(
            map((vendorHardwareData) => MetalsActions.LoadVendorHardwareDataByHardwareItemIds.success({ vendorHardwareData })),
            catchError((error) => of(MetalsActions.LoadVendorHardwareDataByHardwareItemIds.failure({ error })))
        ))
    ));

    SetHardwareInventoryTransaction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MetalsActions.SetHardwareInventoryTransaction.initial),
            withLatestFrom(this.store.select(CoreSelectors.getCurrentUser)),
            mergeMap(([{ hardwareId, qtyChange, comment }, curentUser]) => this.metalsApiService.newHardwareInventoryTransaction(
                hardwareId,
                qtyChange,
                comment,
                curentUser
            ).pipe(
                map((transaction: InventoryTransaction) => MetalsActions.SetHardwareInventoryTransaction.success({ transaction })),
                catchError((error) => of(MetalsActions.SetHardwareInventoryTransaction.failure({ error })))
            ))
        )
    )

    LoadRecentHardwareInventoryTransactions$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadRecentHardwareInventoryTransactions.initial),
        mergeMap(() => this.metalsApiService.loadRecentHardwareInventoryTransactions().pipe(
            map((transactions) => MetalsActions.LoadRecentHardwareInventoryTransactions.success({ transactions })),
            catchError((error) => of(MetalsActions.LoadRecentHardwareInventoryTransactions.failure({ error })))
        ))
    ));

    SetProduct$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MetalsActions.SetProduct.initial),
            mergeMap(({ product }) => this.metalsApiService.setProduct(product).pipe(
                map(() => MetalsActions.SetProduct.success({ product })),
                catchError((error) => of(MetalsActions.SetProduct.failure({ error })))
            ))
        )
    )

    SetBillOfMaterialsLineItems$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.SetBillOfMaterialsLineItems.initial),
        mergeMap(({ lineItems }) => this.metalsApiService.setBillOfMaterialsLineItems(lineItems).pipe(
            map(() => MetalsActions.SetBillOfMaterialsLineItems.success({ lineItems })),
            catchError((error) => of(MetalsActions.SetBillOfMaterialsLineItems.failure({ error })))
        ))
    ));

    SetProductVersion$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MetalsActions.SetProductVersion.initial),
            mergeMap(({ productVersion }) => this.metalsApiService.setProductVersion(productVersion).pipe(
                map(() => MetalsActions.SetProductVersion.success({ productVersion })),
                catchError((error) => of(MetalsActions.SetProductVersion.failure({ error })))
            ))
        )
    )

    LoadProducts$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadProducts.initial),
        mergeMap(() => this.metalsApiService.loadProducts().pipe(
            map((products) => MetalsActions.LoadProducts.success({ products })),
            catchError((error) => of(MetalsActions.LoadProducts.failure({ error })))
        ))
    ));

    LoadReleasedProductVersions$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadReleasedProductVersions.initial),
        mergeMap(() => this.metalsApiService.loadReleasedProductVersions().pipe(
            map((releasedProductVersions) => MetalsActions.LoadReleasedProductVersions.success({ releasedProductVersions })),
            catchError((error) => of(MetalsActions.LoadReleasedProductVersions.failure({ error })))
        ))
    ));

    LoadReleasedAndDraftProductVersions$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadReleasedAndDraftProductVersions.initial),
        mergeMap(() => this.metalsApiService.loadReleasedAndDraftProductVersions().pipe(
            map((releasedAndDraftProductVersions) => MetalsActions.LoadReleasedAndDraftProductVersions.success({ releasedAndDraftProductVersions })),
            catchError((error) => of(MetalsActions.LoadReleasedAndDraftProductVersions.failure({ error })))
        ))
    ))

    LoadProductById$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadProductById.initial),
        mergeMap(({ productId }) => this.metalsApiService.loadProductById(productId).pipe(
            map((product) => MetalsActions.LoadProductById.success({ product })),
            catchError((error) => of(MetalsActions.LoadProductById.failure({ error })))
        ))
    ));

    LoadProductsByIds$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadProductsByIds.initial),
        mergeMap(({ productIds }) => this.metalsApiService.loadProductsByIds(productIds).pipe(
            map((products) => MetalsActions.LoadProductsByIds.success({ products })),
            catchError((error) => of(MetalsActions.LoadProductsByIds.failure({ error })))
        ))
    ));

    LoadProductVersionsByIds$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadProductVersionsByIds.initial),
        mergeMap(({ productVersionIds }) => this.metalsApiService.loadProductVersionsByIds(productVersionIds).pipe(
            map((productVersions) => MetalsActions.LoadProductVersionsByIds.success({ productVersions })),
            catchError((error) => of(MetalsActions.LoadProductVersionsByIds.failure({ error })))
        ))
    ))


    // ============================================================================
    // ============================================================================
    // ============================================================================


    LoadActivePlasticsFinishingTasks$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadActiveStampingTasks.initial),
        mergeMap(() => this.metalsApiService.loadActiveStampingTasks().pipe(
            map((tasks) => MetalsActions.LoadActiveStampingTasks.success({ tasks })),
            catchError(({ error }) => of(MetalsActions.LoadActiveStampingTasks.failure({ error })))
        ))
    ));

    LoadPlasticsFinishingStations$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadStampingStations.initial),
        mergeMap(() => this.metalsApiService.loadStampingStations().pipe(
            map((stations) => MetalsActions.LoadStampingStations.success({ stations })),
            catchError(({ error }) => of(MetalsActions.LoadStampingStations.failure({ error })))
        ))

    ));



    SetStampingTask$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.SetStampingTask.initial),
        withLatestFrom(this.store.select(CoreSelectors.getCurrentUser)),
        mergeMap(([{ task }, currentUser]) => {
            const updatedTask: StampingTask = {
                ...task,
                updatedById: currentUser.id,
                updatedOn: new Date(),
            }
            return this.metalsApiService.setStampingTask(updatedTask).pipe(
                map(() => MetalsActions.SetStampingTask.success({ task: updatedTask })),
                catchError(({ error }) => of(MetalsActions.SetStampingTask.failure({ error })))
            )
        })
    ));



    SetFinishingStation$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.SetStampingStation.initial),
        mergeMap(({ station }) => this.metalsApiService.setStampingStation(station).pipe(
            map((stations) => MetalsActions.SetStampingStation.success({ station })),
            catchError(({ error }) => of(MetalsActions.SetStampingStation.failure({ error })))
        ))
    ));

    SetVendorHardwareData$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.SetVendorHardwareData.initial),
        mergeMap(({ vendorHardwareData }) => this.metalsApiService.setVendorHardwareData(vendorHardwareData).pipe(
            map(() => MetalsActions.SetVendorHardwareData.success({ vendorHardwareData })),
            catchError((error) => of(MetalsActions.SetVendorHardwareData.failure({ error })))
        ))
    ))

    LoadProductSetups$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadProductSetups.initial),
        mergeMap(() => this.metalsApiService.loadProductSetups().pipe(
            map((productSetups) => MetalsActions.LoadProductSetups.success({ productSetups })),
            catchError((error) => of(MetalsActions.LoadProductSetups.failure({ error })))
        ))
    ));

    LoadProductSetupById$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadProductSetupById.initial),
        mergeMap(({ productSetupId }) => this.metalsApiService.loadProductSetupById(productSetupId).pipe(
            map((productSetup) => MetalsActions.LoadProductSetupById.success({ productSetup })),
            catchError((error) => of(MetalsActions.LoadProductSetupById.failure({ error })))
        ))
    ));

    SetProductSetup$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.SetProductSetup.initial),
        mergeMap(({ productSetup }) => this.metalsApiService.setProductSetup(productSetup).pipe(
            map(() => MetalsActions.SetProductSetup.success({ productSetup })),
            catchError((error) => of(MetalsActions.SetProductSetup.failure({ error })))
        ))
    ));

    DeleteProductSetup$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.DeleteProductSetup.initial),
        mergeMap(({ productSetupId }) => this.metalsApiService.deleteProductSetup(productSetupId).pipe(
            map(() => MetalsActions.DeleteProductSetup.success({ productSetupId })),
            catchError((error) => of(MetalsActions.DeleteProductSetup.failure({ error })))
        ))
    ));

    LoadToolRoomMachines$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.LoadToolRoomMachines.initial),
        mergeMap(() => this.metalsApiService.loadToolRoomMachines().pipe(
            map((machines) => MetalsActions.LoadToolRoomMachines.success({ machines })),
            catchError((error) => of(MetalsActions.LoadToolRoomMachines.failure({ error })))
        ))
    ));

    SetToolRoomMachine$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.SetToolRoomMachine.initial),
        mergeMap(({ machine }) => this.metalsApiService.setToolRoomMachine(machine).pipe(
            map(() => MetalsActions.SetToolRoomMachine.success({ machine })),
            catchError((error) => of(MetalsActions.SetToolRoomMachine.failure({ error })))
        ))
    ));

    DeleteToolRoomMachine$ = createEffect(() => this.actions$.pipe(
        ofType(MetalsActions.DeleteToolRoomMachine.initial),
        mergeMap(({ machineId }) => this.metalsApiService.deleteToolRoomMachine(machineId).pipe(
            map(() => MetalsActions.DeleteToolRoomMachine.success({ machineId })),
            catchError((error) => of(MetalsActions.DeleteToolRoomMachine.failure({ error })))
        ))
    ));

}